import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormControlLabel,
    Switch,
    Autocomplete,
    Chip,
    IconButton,
    Alert,
    Tooltip,
    InputAdornment,
    Stepper,
    Step,
    StepButton,
    Tabs,
    Tab,
    Paper,
    Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Axios from "axios";
import { useSignOut } from "react-auth-kit";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { getAuthHeader } from "../../utils/authToken";
import Loading from "../../components/Loading";
import ProgramExamsPanel from "./ProgramExamsPanel";
import ProgramV2TestsPanel from "./ProgramV2TestsPanel";
import HomeworkSection from "./HomeworkSection";
import ReportScheduleSection from "./ReportScheduleSection";
import PrepCalendar from "./PrepCalendar";
import { programScheduleEvents, earliestEventDate } from "./prepCalendarEvents";
import { Field } from "./prepUi";

const PROGRAM_TYPES = ["SAT", "ACT", "AP"];
const QB_TYPES = ["dsat", "act", "ap", "psat"];
// "Type" (program_type) is a coarse label the admin sets separately from "QB type" (ett_qb_type) —
// the field that actually drives questions, scoring, and the test name families see in reports/emails.
// They should agree; this maps each QB type to the Type it implies so the editor can warn on a mismatch.
const QB_TYPE_TO_PROGRAM_TYPE = { dsat: "SAT", sat: "SAT", act: "ACT", ap: "AP", psat: "SAT" };
// season is stored as an int (see utils/CourseDuration).
const SEASON_LABELS = { 1: "Spring", 2: "Summer", 3: "Fall", 4: "Winter" };
const semesterLabel = (s) => `${SEASON_LABELS[s.season] || `Season ${s.season}`} ${s.year}`;
const PROGRAMS_BASE = `${process.env.REACT_APP_URL}/api/v1/prep-programs`;
const authCfg = () => ({ headers: { Authorization: getAuthHeader() } });

// The four sections of a prep program, in dependency order: Basics + Courses are
// saved together; Tests + Homework persist immediately but require a saved program.
const SECTIONS = [
    { key: "basics", label: "Basics" },
    { key: "courses", label: "Courses" },
    { key: "tests", label: "Tests" },
    { key: "homework", label: "Homework" },
    { key: "reports", label: "Reports" },
];

// Debounced course-search Autocomplete. When a semester is chosen the search is scoped
// to it (and can browse the semester's courses before typing); otherwise it needs 2+ chars.
const CourseAutocomplete = ({ value, onChange, label, semesterId }) => {
    const [options, setOptions] = useState(value ? [value] : []);
    const [input, setInput] = useState("");
    const timer = useRef(null);

    useEffect(() => {
        if (!semesterId && (!input || input.length < 2)) return undefined;
        clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
            try {
                const res = await Axios.get(`${PROGRAMS_BASE}/course-search`, {
                    ...authCfg(),
                    params: { q: input, ...(semesterId ? { semester_id: semesterId } : {}) },
                });
                setOptions(res.data);
            } catch (e) {
                /* ignore search errors */
            }
        }, 300);
        return () => clearTimeout(timer.current);
    }, [input, semesterId]);

    return (
        <Autocomplete
            size="small"
            sx={{ minWidth: 280 }}
            options={options}
            value={value || null}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            getOptionLabel={(o) => (o ? `${o.course_title}${o.code ? ` (${o.code})` : ""}` : "")}
            onInputChange={(e, v) => setInput(v)}
            onChange={(e, v) => onChange(v)}
            renderInput={(params) => <TextField {...params} label={label} />}
        />
    );
};

const blankProgram = {
    name: "", program_type: "SAT", ett_qb_type: "dsat", description: "",
    is_active: true, mastery_threshold_default: 100, mastery_cycles_default: 2, show_exams_on_homework: true,
};

const PrepProgramEditor = () => {
    const { uuid } = useParams();
    const signOut = useSignOut();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    // Create flow shows a Stepper; edit flow shows Tabs. Decided once at mount —
    // the program is created in-place during the wizard (no route change) so this
    // stays "wizard" through all four steps.
    const [isWizard] = useState(!uuid);
    const [programUuid, setProgramUuid] = useState(uuid || null);
    const [step, setStep] = useState(0);
    const [homeworkCourseTab, setHomeworkCourseTab] = useState(0); // sub-tab within Homework

    const [program, setProgram] = useState(blankProgram);
    const [practiceCourses, setPracticeCourses] = useState([]); // [{programCourseId?, course}]
    const [subjectCourses, setSubjectCourses] = useState([]); // [{programCourseId?, course, ett_qb_subject_id, label}]
    const [taxonomySubjects, setTaxonomySubjects] = useState([]); // [{subject_id, subject}]
    const [semesters, setSemesters] = useState([]); // [{id, season, year}] for the course-search filter
    const [courseSemesterId, setCourseSemesterId] = useState(""); // scopes the course pickers
    const [exams, setExams] = useState([]);
    const [rounds, setRounds] = useState([]);
    const [loading, setLoading] = useState(!!uuid);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    };

    // Load (or refresh) a program's full detail. Takes the target uuid explicitly so
    // it can be called right after create — before the programUuid state has settled.
    // Does NOT raise the full-page spinner: refreshes (after an exam/round change)
    // update in place without unmounting the open Tests/Homework panels.
    const loadDetail = useCallback(async (targetUuid) => {
        if (!targetUuid) return;
        try {
            const res = await Axios.get(`${PROGRAMS_BASE}/${targetUuid}`, authCfg());
            const { program: prog, courses, exams: ex, rounds: rd } = res.data;
            setProgram({
                name: prog.name || "",
                program_type: prog.program_type || "SAT",
                ett_qb_type: prog.ett_qb_type || "dsat",
                description: prog.description || "",
                is_active: prog.is_active ?? true,
                mastery_threshold_default: prog.mastery_threshold_default ?? 100,
                mastery_cycles_default: prog.mastery_cycles_default ?? 2,
                show_exams_on_homework: prog.show_exams_on_homework ?? true,
            });
            setPracticeCourses(
                courses.filter((c) => c.role === "PRACTICE").map((c) => ({ programCourseId: c.id, course: c.course }))
            );
            setSubjectCourses(
                courses.filter((c) => c.role === "SUBJECT").map((c) => ({
                    programCourseId: c.id, course: c.course, ett_qb_subject_id: c.ett_qb_subject_id,
                }))
            );
            setExams(ex || []);
            setRounds(rd || []);
        } catch (e) {
            if (e.response?.status === 401) handleLogout();
            else setError("Failed to load program");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        localStorage.setItem("selectedMenu", "prepPrograms");
        if (uuid) loadDetail(uuid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Semesters for the course-search filter (newest first, per the endpoint's order).
    useEffect(() => {
        (async () => {
            try {
                const res = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/semesters`, authCfg());
                setSemesters(res.data || []);
            } catch (e) {
                setSemesters([]);
            }
        })();
    }, []);

    // Taxonomy subjects depend on ett_qb_type.
    useEffect(() => {
        let active = true;
        if (!program.ett_qb_type) {
            setTaxonomySubjects([]);
            return undefined;
        }
        (async () => {
            try {
                const res = await Axios.get(`${PROGRAMS_BASE}/taxonomy`, { ...authCfg(), params: { qb_type: program.ett_qb_type } });
                if (active) setTaxonomySubjects((res.data || []).map((s) => ({ subject_id: s.subject_id, subject: s.subject })));
            } catch (e) {
                if (active) setTaxonomySubjects([]);
            }
        })();
        return () => {
            active = false;
        };
    }, [program.ett_qb_type]);

    // Combined-class detection: a course id used in more than one role/row.
    const courseIdCounts = {};
    [...practiceCourses, ...subjectCourses].forEach((row) => {
        const id = row.course?.id;
        if (id) courseIdCounts[id] = (courseIdCounts[id] || 0) + 1;
    });
    const isCombined = (row) => row.course?.id && courseIdCounts[row.course.id] > 1;

    const setProg = (patch) => setProgram((p) => ({ ...p, ...patch }));

    const buildCoursesPayload = () => {
        const out = [];
        // Send the prep_program_course id (when this row maps to a saved one) so the server can
        // match by id — required for a "combined" course backing two SUBJECT rows (same course
        // + role, different ETT subject); matching by course_id+role alone would merge them.
        practiceCourses.forEach((r) => {
            if (r.course?.id) out.push({ id: r.programCourseId ?? null, course_id: r.course.id, role: "PRACTICE" });
        });
        subjectCourses.forEach((r) => {
            if (r.course?.id) out.push({ id: r.programCourseId ?? null, course_id: r.course.id, role: "SUBJECT", ett_qb_subject_id: r.ett_qb_subject_id ?? null });
        });
        return out;
    };

    // Persist Basics + Courses (POST on first save, PUT thereafter). Returns true on
    // success. Reloads detail so newly-created subject courses get their ids (needed
    // by the Homework section).
    const saveBasicsAndCourses = async () => {
        setError("");
        if (!program.name.trim()) {
            setError("Name is required");
            return false;
        }
        const courses = buildCoursesPayload();
        if (!courses.some((c) => c.role === "PRACTICE")) {
            setError("At least one practice course is required");
            return false;
        }

        setSaving(true);
        try {
            const body = { ...program, courses };
            if (programUuid) {
                await Axios.put(`${PROGRAMS_BASE}/${programUuid}`, body, authCfg());
                await loadDetail(programUuid);
            } else {
                const res = await Axios.post(PROGRAMS_BASE, body, authCfg());
                const newUuid = res.data.program.uuid;
                setProgramUuid(newUuid);
                await loadDetail(newUuid);
            }
            enqueueSnackbar(programUuid ? "Program saved" : "Program created", { variant: "success" });
            return true;
        } catch (e) {
            if (e.response?.status === 401) handleLogout();
            else setError(e.response?.data?.error || "Failed to save program");
            return false;
        } finally {
            setSaving(false);
        }
    };

    const goToStep = (i) => {
        setError("");
        setStep(i);
    };

    // Wizard "primary" action per step. Basics just validates + advances; Courses
    // saves (creating the program) before advancing; Tests/Homework persist on their
    // own, so those steps only navigate.
    const handleWizardPrimary = async () => {
        if (step === 0) {
            if (!program.name.trim()) return setError("Name is required");
            return goToStep(1);
        }
        if (step === 1) {
            const ok = await saveBasicsAndCourses();
            if (ok) goToStep(2);
            return undefined;
        }
        if (step === 2) return goToStep(3);
        if (step === 3) return goToStep(4);
        return navigate("/admin/prepPrograms");
    };

    const wizardPrimaryLabel =
        step === 0 ? "Next"
            : step === 1 ? (programUuid ? "Save & continue" : "Create program")
                : step === 2 || step === 3 ? "Next"
                    : "Finish";

    // In the wizard, Tests/Homework/Reports are unreachable until the program is created.
    const maxStep = programUuid ? 4 : 1;

    // ---- Section renderers (shared by wizard + tabs) ----

    const renderBasics = () => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Field label="Name & type">
                <Box display="flex" gap={2} flexWrap="wrap">
                    <TextField label="Name" size="small" sx={{ minWidth: 240 }}
                        value={program.name} onChange={(e) => setProg({ name: e.target.value })} />
                    <FormControl size="small" sx={{ width: 120 }}>
                        <InputLabel>Type</InputLabel>
                        <Select label="Type" value={program.program_type} onChange={(e) => setProg({ program_type: e.target.value })}>
                            {PROGRAM_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ width: 120 }}>
                        <InputLabel>QB type</InputLabel>
                        <Select label="QB type" value={program.ett_qb_type} onChange={(e) => setProg({ ett_qb_type: e.target.value })}>
                            {QB_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Box>
                        <FormControlLabel
                            control={<Switch checked={program.is_active} onChange={(e) => setProg({ is_active: e.target.checked })} />}
                            label="Active (automation)"
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                            Controls homework auto-release &amp; recurring reports. To hide a finished program from the list, use Archive (it stays visible here).
                        </Typography>
                    </Box>
                </Box>
                {(() => {
                    const expected = QB_TYPE_TO_PROGRAM_TYPE[String(program.ett_qb_type || "").toLowerCase()];
                    if (!expected || program.program_type === expected) return null;
                    return (
                        <Alert severity="warning" sx={{ mt: 1.5 }}>
                            <b>Type</b> is <b>{program.program_type}</b> but <b>QB type</b> is{" "}
                            <b>{program.ett_qb_type}</b>, which is a <b>{expected}</b> program. QB type drives the
                            questions, scoring, and the test name families see in reports and homework emails —
                            set <b>Type</b> to <b>{expected}</b> so they match.
                        </Alert>
                    );
                })()}
            </Field>

            <Field label="Homework default">
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
                    <TextField
                        label="Practiced threshold"
                        type="number"
                        size="small"
                        sx={{ width: 220 }}
                        value={program.mastery_threshold_default}
                        onChange={(e) => setProg({ mastery_threshold_default: Number(e.target.value) })}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title="A Q-bank chapter is flagged 'N+ practiced' once the student has answered this many questions in it (cumulative attempts, re-solves included) — it's a practice-volume milestone, not an accuracy measure. Such chapters are skipped when homework is auto-assigned. Default 100.">
                                        <InfoOutlinedIcon fontSize="small" color="action" />
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        label="Mastery cycles (default)"
                        type="number"
                        size="small"
                        sx={{ width: 220 }}
                        value={program.mastery_cycles_default}
                        onChange={(e) => setProg({ mastery_cycles_default: Number(e.target.value) })}
                        helperText="Max full passes before a small chapter retires (1–10)"
                    />
                </Box>
            </Field>

            <Field label="Classroom display">
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <FormControlLabel
                        control={<Switch checked={program.show_exams_on_homework ?? true} onChange={(e) => setProg({ show_exams_on_homework: e.target.checked })} />}
                        label="Show practice tests on the Homework page"
                    />
                    <Typography variant="caption" color="text.secondary">
                        Practice tests always appear on the classroom Tests page. Turn this off to keep them on the Tests page only.
                    </Typography>
                </Box>
            </Field>

            <Field label="Description">
                <TextField size="small" fullWidth multiline minRows={2}
                    value={program.description} onChange={(e) => setProg({ description: e.target.value })} />
            </Field>
        </Box>
    );

    const renderCourses = () => (
        <Box>
            <FormControl size="small" sx={{ minWidth: 240, mb: 2 }}>
                <InputLabel>Semester (scopes course search)</InputLabel>
                <Select
                    label="Semester (scopes course search)"
                    value={courseSemesterId}
                    onChange={(e) => setCourseSemesterId(e.target.value)}
                >
                    <MenuItem value=""><em>All semesters</em></MenuItem>
                    {semesters.map((s) => (
                        <MenuItem key={s.id} value={s.id}>{semesterLabel(s)}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Pick a semester first to narrow the course pickers — courses can share titles across semesters.
            </Typography>

            <Typography variant="subtitle2">Practice course(s)</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                The class where students sit full practice exams. The engine measures each student's
                weakness from these exams' results; the exams themselves are added under "Tests".
            </Typography>
            {practiceCourses.map((row, i) => (
                <Box key={`p${i}`} display="flex" alignItems="center" gap={1} mb={1}>
                    <CourseAutocomplete
                        label="Practice course"
                        value={row.course}
                        semesterId={courseSemesterId}
                        onChange={(course) => setPracticeCourses((prev) => prev.map((r, idx) => (idx === i ? { ...r, course } : r)))}
                    />
                    {isCombined(row) && <Chip size="small" color="warning" label="combined" />}
                    <IconButton size="small" onClick={() => setPracticeCourses((prev) => prev.filter((_, idx) => idx !== i))}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={() => setPracticeCourses((prev) => [...prev, { course: null }])}>
                Add practice course
            </Button>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2">Subject courses</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Per-subject classes (one per test subject) that receive question-bank homework.
                Map each to its ETT subject so weak chapters can be auto-assigned and tracked.
            </Typography>
            {!program.ett_qb_type && (
                <Typography variant="caption" color="text.secondary">Set a QB type under Basics to pick ETT subjects.</Typography>
            )}
            {subjectCourses.map((row, i) => (
                <Box key={`s${i}`} display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                    <CourseAutocomplete
                        label="Subject course"
                        value={row.course}
                        semesterId={courseSemesterId}
                        onChange={(course) => setSubjectCourses((prev) => prev.map((r, idx) => (idx === i ? { ...r, course } : r)))}
                    />
                    <FormControl size="small" sx={{ minWidth: 180 }} disabled={!program.ett_qb_type}>
                        <InputLabel>ETT subject</InputLabel>
                        <Select
                            label="ETT subject"
                            value={row.ett_qb_subject_id ?? ""}
                            onChange={(e) => setSubjectCourses((prev) => prev.map((r, idx) => (idx === i ? { ...r, ett_qb_subject_id: e.target.value } : r)))}
                        >
                            {taxonomySubjects.map((s) => <MenuItem key={s.subject_id} value={s.subject_id}>{s.subject}</MenuItem>)}
                        </Select>
                    </FormControl>
                    {isCombined(row) && <Chip size="small" color="warning" label="combined" />}
                    <IconButton size="small" onClick={() => setSubjectCourses((prev) => prev.filter((_, idx) => idx !== i))}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={() => setSubjectCourses((prev) => [...prev, { course: null, ett_qb_subject_id: "" }])}>
                Add subject course
            </Button>
        </Box>
    );

    const renderTests = () => {
        if (!programUuid) {
            return <Typography variant="body2" color="text.secondary">Create the program first to add tests.</Typography>;
        }
        // Saved program courses (with their prep_program_course id) — the target for library tests.
        const v2Courses = [
            ...practiceCourses.filter((c) => c.programCourseId).map((c) => ({ id: c.programCourseId, role: "PRACTICE", label: c.course?.course_title, course_title: c.course?.course_title })),
            ...subjectCourses.filter((c) => c.programCourseId).map((c) => ({ id: c.programCourseId, role: "SUBJECT", label: c.label || c.course?.course_title, course_title: c.course?.course_title })),
        ];
        return (
            <>
                <ProgramExamsPanel programUuid={programUuid} programType={program.program_type} onChange={() => loadDetail(programUuid)} />
                <Divider sx={{ my: 4 }} />
                <ProgramV2TestsPanel programUuid={programUuid} courses={v2Courses} onChange={() => loadDetail(programUuid)} />
            </>
        );
    };

    const renderHomework = () => {
        if (!programUuid) {
            return <Typography variant="body2" color="text.secondary">Create the program first to configure homework.</Typography>;
        }
        const subjects = subjectCourses.filter((c) => c.programCourseId);
        if (subjects.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    Add subject courses under "Courses" (and save) to configure homework.
                </Typography>
            );
        }
        const activeIdx = Math.min(homeworkCourseTab, subjects.length - 1);
        const active = subjects[activeIdx];
        // Subject is the headline on each tab — when the same course is reused for both
        // Math and R&W (combined), the course label is identical, so the subject is the
        // only way to tell the tabs apart.
        const subjectName = (id) => taxonomySubjects.find((s) => s.subject_id === id)?.subject;
        return (
            <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Question-bank homework per subject course. Rounds and recurrences save immediately.
                </Typography>
                <Tabs
                    value={activeIdx}
                    onChange={(e, v) => setHomeworkCourseTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
                >
                    {subjects.map((c) => {
                        const subj = subjectName(c.ett_qb_subject_id);
                        const courseLabel = c.label || c.course?.course_title || "";
                        return (
                            <Tab
                                key={c.programCourseId}
                                sx={{ textTransform: "none", alignItems: "flex-start", minHeight: 56 }}
                                label={
                                    <Box sx={{ textAlign: "left" }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                            {subj || courseLabel || "Subject"}
                                        </Typography>
                                        {subj && courseLabel && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.1 }}>
                                                {courseLabel}
                                            </Typography>
                                        )}
                                    </Box>
                                }
                            />
                        );
                    })}
                </Tabs>
                <HomeworkSection
                    key={active.programCourseId}
                    programUuid={programUuid}
                    subjectCourse={{ id: active.programCourseId, ett_qb_subject_id: active.ett_qb_subject_id, label: active.label, course: active.course }}
                    programExams={exams}
                    qbType={program.ett_qb_type}
                    rounds={rounds}
                    onChange={() => loadDetail(programUuid)}
                />
            </>
        );
    };

    const renderReports = () => {
        if (!programUuid) {
            return <Typography variant="body2" color="text.secondary">Create the program first to set up reports.</Typography>;
        }
        // Recommend the homework-round release weekday as the report day.
        const firstRelease = (rounds || []).map((r) => r.release_date).filter(Boolean).sort()[0];
        // Weekday in NY (0=Sun…6=Sat) to match the server's NY-based report scan — not the
        // admin's browser timezone, which could be off by a day.
        const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const recommendedDay = firstRelease
            ? DOW.indexOf(new Date(firstRelease).toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "short" }))
            : null;
        return <ReportScheduleSection programUuid={programUuid} recommendedDay={recommendedDay} />;
    };

    const renderCalendar = () => {
        if (!programUuid) {
            return <Typography variant="body2" color="text.secondary">Create the program first to see its schedule.</Typography>;
        }
        // Prefer the ETT subject name for each round's label, falling back to the
        // course label / title.
        const subjectLabels = {};
        subjectCourses.forEach((c) => {
            if (!c.programCourseId) return;
            const subj = taxonomySubjects.find((s) => s.subject_id === c.ett_qb_subject_id)?.subject;
            subjectLabels[c.programCourseId] = subj || c.label || c.course?.course_title || "Homework";
        });
        const events = programScheduleEvents({ exams, rounds, subjectLabels });
        if (events.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    No dated exams or homework yet. Add them under Tests and Homework to see them here.
                </Typography>
            );
        }
        return <PrepCalendar events={events} initialDate={earliestEventDate(events)} />;
    };

    const renderSection = (idx) => [renderBasics, renderCourses, renderTests, renderHomework, renderReports, renderCalendar][idx]();

    // The Calendar tab is a review view, so it only appears when editing an existing
    // program — not as a step in the create wizard.
    const sections = isWizard ? SECTIONS : [...SECTIONS, { key: "calendar", label: "Calendar" }];

    if (loading) return <Loading />;

    return (
        <Box m="20px">
            <Box display="flex" alignItems="center" gap={1} mb="20px">
                <IconButton onClick={() => navigate("/admin/prepPrograms")}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" fontWeight="bold">
                    {isWizard ? "New Program" : "Edit Program"}
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {isWizard ? (
                <Stepper nonLinear activeStep={step} sx={{ mb: 3 }}>
                    {sections.map((s, i) => (
                        <Step key={s.key} completed={!!programUuid && i < 2}>
                            <StepButton color="inherit" onClick={() => goToStep(i)} disabled={i > maxStep}>
                                {s.label}
                            </StepButton>
                        </Step>
                    ))}
                </Stepper>
            ) : (
                <Tabs value={step} onChange={(e, v) => goToStep(v)} sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
                    {sections.map((s) => <Tab key={s.key} label={s.label} />)}
                </Tabs>
            )}

            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, minHeight: 200 }}>
                {renderSection(step)}
            </Paper>

            {isWizard ? (
                <Box sx={{ mt: 4, display: "flex", gap: 1 }}>
                    <Button disabled={step === 0 || saving} onClick={() => goToStep(step - 1)}>
                        Back
                    </Button>
                    <Button variant="contained" onClick={handleWizardPrimary} disabled={saving}>
                        {wizardPrimaryLabel}
                    </Button>
                </Box>
            ) : (
                step <= 1 && (
                    <Box sx={{ mt: 4 }}>
                        <Button variant="contained" onClick={saveBasicsAndCourses} disabled={saving}>
                            Save program
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                            Basics &amp; Courses save together. Tests &amp; Homework save instantly.
                        </Typography>
                    </Box>
                )
            )}
        </Box>
    );
};

export default PrepProgramEditor;
